import React, { useState, useMemo } from 'react';
import { diffLines } from 'diff';

export default function DiffViewer({ original = '', revised = '', collapsedThreshold = 12, pageSize = 10, maxHeight = 360 }) {
  // collapsedThreshold: minimum lines in a context block to trigger the placeholder
  const [visibleMap, setVisibleMap] = useState({}); // idx -> number of visible lines
  const [fileCollapsed, setFileCollapsed] = useState(false);

  const parts = useMemo(() => {
    try {
      return diffLines(original || '', revised || '');
    } catch (e) {
      return [{ value: original || '' }];
    }
  }, [original, revised]);

  const blocks = useMemo(() => parts.map((p, idx) => {
    const rawLines = (p.value || '').split('\n');
    const lines = rawLines.length && rawLines[rawLines.length - 1] === '' ? rawLines.slice(0, -1) : rawLines;
    const type = p.added ? 'added' : p.removed ? 'removed' : 'context';
    return { idx, type, lines };
  }), [parts]);

  const addedCount = blocks.reduce((s, b) => s + (b.type === 'added' ? b.lines.length : 0), 0);
  const removedCount = blocks.reduce((s, b) => s + (b.type === 'removed' ? b.lines.length : 0), 0);

  const showMore = (idx) => {
    setVisibleMap(prev => {
      const current = prev[idx] || 0;
      const total = blocks.find(b => b.idx === idx)?.lines.length || 0;
      const next = Math.min(total, current + pageSize);
      return { ...prev, [idx]: next };
    })
  };

  const collapseBlock = (idx) => {
    setVisibleMap(prev => ({ ...prev, [idx]: 0 }));
  }

  if (fileCollapsed) {
    return (
      <div style={{ padding: 8, background: '#fff', borderRadius: 8, border: '1px solid #e6e6e6' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 700 }}>Prompt (colapsado)</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ color: '#6c757d', fontSize: 13 }}>{addedCount} añadidas · {removedCount} eliminadas</div>
            <button className="btn btn-sm btn-outline-primary" onClick={() => setFileCollapsed(false)}>Expandir</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: '#f8fafc', borderBottom: '1px solid #eef2f7' }}>
        <div style={{ fontWeight: 700 }}>Comparación de prompts</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ color: '#6c757d', fontSize: 13 }}>{addedCount} añadidas · {removedCount} eliminadas</div>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => setFileCollapsed(true)}>Colapsar archivo</button>
        </div>
      </div>

      <div style={{ maxHeight, overflowY: 'auto', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, "Roboto Mono", monospace', fontSize: 13 }}>
        {blocks.length === 0 && <div style={{ padding: 12, color: '#6b7280' }}>Sin cambios.</div>}

        {blocks.map((b) => {
          if (b.type === 'context') {
            const total = b.lines.length;
            const visible = visibleMap[b.idx] || 0;
            // If block is large, show only placeholder initially
            if (total > collapsedThreshold && visible === 0) {
              return (
                <div key={b.idx} style={{ padding: 12, background: '#fff' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ color: '#6b7280' }}>{total} líneas sin cambios</div>
                    <div>
                      <button className="btn btn-sm btn-link" onClick={() => showMore(b.idx)}>{`Mostrar ${Math.min(pageSize, total)} líneas`}</button>
                    </div>
                  </div>
                </div>
              );
            }

            // show paginated content when visible > 0
            const linesToShow = visible > 0 ? b.lines.slice(0, visible) : b.lines;
            return (
              <div key={b.idx} style={{ padding: 8, background: '#fff' }}>
                {linesToShow.map((ln, i) => (
                  <div key={i} style={{ padding: '2px 8px', color: '#374151' }}><span style={{ color: '#9ca3af', marginRight: 8 }}>&nbsp;</span>{ln}</div>
                ))}
                {visible > 0 && visible < total && (
                  <div style={{ textAlign: 'center', padding: 8 }}>
                    <button className="btn btn-sm btn-link" onClick={() => showMore(b.idx)}>{`Mostrar siguientes ${Math.min(pageSize, total - visible)} líneas`}</button>
                    <button className="btn btn-sm btn-link" onClick={() => collapseBlock(b.idx)} style={{ marginLeft: 8 }}>Colapsar</button>
                  </div>
                )}
              </div>
            );
          }

          if (b.type === 'removed') {
            return (
              <div key={b.idx} style={{ padding: 8, background: '#fff5f6' }}>
                {b.lines.map((l, i) => <div key={i} style={{ padding: '2px 8px', color: '#b91c1c' }}><span style={{ color: '#b91c1c', marginRight: 8 }}>-</span>{l}</div>)}
              </div>
            );
          }

          // added
          return (
            <div key={b.idx} style={{ padding: 8, background: '#ecfdf5' }}>
              {b.lines.map((l, i) => <div key={i} style={{ padding: '2px 8px', color: '#064e3b' }}><span style={{ color: '#064e3b', marginRight: 8 }}>+</span>{l}</div>)}
            </div>
          );
        })}
      </div>
    </div>
  );
}
